import { logError } from '@qogita/logging'

import { developmentDisruptError } from '#src/utils/developmentError'

import { validateStringArray } from './array'
import { FieldError } from './errors'

type ErrorContext = {
  status: string
  fieldError?: string[]
  // Error context can be anything that we want to send to sentry
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>

type GetErrorMessage = {
  [status: number]: (response: Response) => string | Promise<string>
}

type Props = {
  /**
   * Name of the function that is being called. It will be used to log the error.
   */
  name: string
  /**
   * Response from the API.
   */
  response: Response
  /**
   * Default error message to be displayed if the response is not successful.
   */
  defaultErrorMessage?: string
  /**
   * Get error message for different status codes.
   */
  getErrorMessage?: GetErrorMessage
  /**
   * Skip the error logging.
   */
  dryRun?: boolean
  /**
   * Prioritize sending nonFieldErrors to the request.
   * This means that if this flag is true and the response contains an array of errors, the thrown error will be the nonFieldErrors.
   */
  prioritizeNonFieldErrors?: boolean
}

const ERROR_FIELDS = ['taxNumber', 'lineNumber', 'zipCode']

export async function getResponse<T>({
  name,
  response,
  defaultErrorMessage,
  getErrorMessage,
  dryRun,
  prioritizeNonFieldErrors,
}: Props): Promise<T> {
  if (response.redirected) {
    /*
      This is a trial to avoid redirects in the API. If this disrupts the flow of work due to false positives,
      we can change this to a devAdviseError.
     */
    developmentDisruptError(
      'getResponse with redirect: Please use the a trailish slash in requests to canary to avoid redirects.',
    )
  }
  if (response.ok) {
    if (response.status === 204) {
      // return null for empty responses
      return null as unknown as T
    }
    return response.json() as unknown as T
  }

  // @ts-expect-error ignore dry run type
  if (dryRun) return

  let errorMessage = defaultErrorMessage

  const getErrorMessageForStatus = getErrorMessage?.[response.status]
  if (getErrorMessageForStatus) {
    errorMessage = await getErrorMessageForStatus(response)
  }

  // continue to log unhandled errors
  const errorContext: ErrorContext = {
    status: response.status.toString(),
  }
  if (errorMessage) {
    errorContext.errorMessage = errorMessage
  }
  try {
    errorContext.data = await response.json()
  } catch {
    // ignore response without json body
  }

  const defaultError = new Error(errorMessage)
  defaultError.name = name

  // The server may return a field called `fieldErrors` in the response
  // containing a list of fields that caused the error. It's used to
  // forward errors from 3rd party APIs, example: Wise.
  let fieldError: FieldError | null = null
  if (errorContext.data?.fieldErrors) {
    const validatedFieldError = validateStringArray(
      errorContext.data.fieldErrors,
    )
    if (validatedFieldError) {
      fieldError = new FieldError(
        errorMessage,
        response.status,
        validatedFieldError,
      )
      fieldError.name = name
    }
  }

  const fieldErrors: string[] = []
  ERROR_FIELDS.forEach((field) => {
    if (errorContext.data?.[field]) {
      fieldErrors.push(field)
    }
  })
  if (fieldErrors.length > 0) {
    fieldError = new FieldError(errorMessage, response.status, fieldErrors)
    fieldError.name = name
  }

  const error = fieldError ?? defaultError
  if (response.status !== 401) {
    logError(error, { 'get-response-context': errorContext })
  }

  /*
    Throws nonFieldErrors from the response
   */
  if (prioritizeNonFieldErrors && errorContext.data?.nonFieldErrors) {
    throw errorContext.data?.nonFieldErrors
  }

  // throw error with user-friendly message for react-query
  throw error
}
